import { Component } from '@theme/component';

/**
 * @typedef {Object} ShippingInfoRefs
 * @property {HTMLElement} textOnetime - One-time purchase text span
 * @property {HTMLElement} textSubscription - Subscription text span
 */

/**
 * Shipping info block.
 *
 * Toggles its `data-purchase-state` attribute (`onetime` | `subscription`)
 * in response to the bubbling `subscription-widget:plan-change` event
 * dispatched by `<subscription-widget-component>`. CSS in the block uses
 * the attribute to swap which text span is visible.
 *
 * On mount, queries for an existing widget in the same container and reads
 * its `selectedSellingPlanId` getter — handles the race where this block
 * connects after the widget already fired its microtask-deferred initial
 * event.
 *
 * @extends {Component<ShippingInfoRefs>}
 */
class ShippingInfo extends Component {
  requiredRefs = ['textOnetime', 'textSubscription'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    if (!container) return;

    container.addEventListener('subscription-widget:plan-change', this.#handlePlanChange, { signal });

    // Sync initial state from any widget already mounted in this container.
    // The widget defers its initial emit via queueMicrotask in its own
    // connectedCallback, so if this block mounts later we need to read the
    // state directly via the public getter.
    const widget = container.querySelector('subscription-widget-component');
    if (widget && typeof widget.selectedSellingPlanId !== 'undefined') {
      const isSubscription = widget.selectedSellingPlanId != null;
      this.#setState(isSubscription ? 'subscription' : 'onetime');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {CustomEvent<{ type: 'subscription' | 'onetime' }>} event
   */
  #handlePlanChange = (event) => {
    const { type } = event.detail;
    if (type === 'subscription' || type === 'onetime') {
      this.#setState(type);
    }
  };

  /**
   * @param {'subscription' | 'onetime'} state
   */
  #setState(state) {
    this.dataset.purchaseState = state;
  }
}

if (!customElements.get('shipping-info')) {
  customElements.define('shipping-info', ShippingInfo);
}
